import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useSpring, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import '../styles/Services.css';
import ScrollProgressIndicator from '../components/ScrollProgressIndicator';
import OurPartnersSection from '../components/OurPartnersSection';
import BookingSection from '../components/BookingSection';

const ServicesPage = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const videoRef = useRef(null);

  const services = [
    {
      id: 1,
      title: 'Web Design',
      image: '/assets/images/website-dev.png',
      description: 'Stunning, responsive websites that captivate your audience and drive results.',
      icon: '🌐',
      details: `
        <h3>Craft Your Digital Masterpiece</h3>
        <p>Our web design service transforms your vision into a stunning online reality. We blend creativity with cutting-edge technology to deliver websites that not only look amazing but also perform flawlessly.</p>
        <h4>Our Design Journey:</h4>
        <ul>
          <li>Discovery: We dive deep into your brand's essence</li>
          <li>Planning: Mapping out your site's blueprint</li>
          <li>Design: Crafting a visual feast for your visitors</li>
          <li>Development: Bringing designs to life with clean code</li>
          <li>Testing: Ensuring perfection across all devices</li>
          <li>Launch: Your grand digital debut</li>
        </ul>
        <h4>Why Choose Us?</h4>
        <ul>
          <li>Responsive designs that shine on every screen</li>
          <li>SEO-friendly architecture for better visibility</li>
          <li>Seamless CMS integration for easy updates</li>
          <li>Custom functionalities tailored to your needs</li>
          <li>Lightning-fast loading speeds</li>
        </ul>
        <h4>Beyond Launch:</h4>
        <p>We offer ongoing support to keep your digital presence fresh and secure. Your success is our continuous mission!</p>
      `,
      color: '#3a86ff'
    },
    {
      id: 2,
      title: 'SEO Optimization',
      image: '/assets/images/SEO-optimizing.jpg',
      description: 'Boost your search rankings and drive organic traffic with our data-driven SEO strategies.',
      icon: '📈',
      details: `
<h3>Climb to the Top of Search Results</h3>

<p>Boost your online visibility with our expert SEO optimization service. We use data-driven strategies to propel your website to the top of search engine rankings, driving organic traffic and growing your digital presence.</p>

<h4>Our SEO Magic:</h4>
<ol>
  <li>Comprehensive Site Audit</li>
  <li>Strategic Keyword Research</li>
  <li>On-Page Optimization</li>
  <li>Technical SEO Enhancements</li>
  <li>Content Strategy Development</li>
  <li>Ethical Link Building</li>
</ol>

<h4>The SEO Advantage:</h4>
<ul>
  <li>Skyrocket your organic search traffic</li>
  <li>Dominate key search terms in your industry</li>
  <li>Enhance user experience and site usability</li>
  <li>Build online authority and credibility</li>
  <li>Achieve better ROI compared to paid ads</li>
</ul>

<h4>Continuous Improvement:</h4>
<p>SEO is an ever-evolving journey. We provide monthly insights and continuously refine our approach to keep you ahead of the curve.</p>
      `,
      color: '#8338ec'
    },
    {
      id: 3,
      title: 'Social Media Management',
      image: '/assets/images/Socialmedia-editing.jpg',
      description: 'Elevate your brand presence across all social platforms with our comprehensive management services.',
      icon: '📱',
      details: `
<h3>Dominate the Social Sphere</h3>

<p>Let us take the reins of your social media presence. From crafting engaging content to growing your follower base, we've got you covered across all major platforms.</p>

<h4>Our Social Strategy:</h4>
<ul>
  <li>Develop a tailored social media gameplan</li>
  <li>Create scroll-stopping content</li>
  <li>Manage posting schedules for peak engagement</li>
  <li>Foster community through active interaction</li>
  <li>Provide in-depth performance analytics</li>
</ul>

<h4>Platforms We Conquer:</h4>
<p>✓ Facebook  ✓ Instagram  ✓ Twitter<br>
✓ LinkedIn  ✓ TikTok    ✓ Pinterest</p>

<h4>Extra Social Boosters:</h4>
<ul>
  <li>Strategic ad campaign management</li>
  <li>Influencer collaborations</li>
  <li>Reputation monitoring and crisis management</li>
</ul>

<h4>Why Go Social With Us?</h4>
<ul>
  <li>Maintain a consistent brand voice</li>
  <li>Skyrocket brand awareness and engagement</li>
  <li>Save valuable time for core business activities</li>
  <li>Stay ahead with the latest social media trends</li>
</ul>
      `,
      color: '#ff006e'
    },
    {
      id: 4,
      title: 'Content Creation',
      image: '/assets/images/content-creation.jpg',
      description: 'Compelling, SEO-optimized content that tells your story and converts visitors into customers.',
      icon: '✍️',
      details: `
<h3>Words That Wow, Content That Converts</h3>

<p>Our content creation service delivers compelling narratives that resonate with your audience and align perfectly with your brand voice. From blog posts to email newsletters, we craft content that engages, informs, and converts.</p>

<h4>Content We Create:</h4>
<ul>
  <li>Engaging Blog Posts and Articles</li>
  <li>Persuasive Website Copy</li>
  <li>Shareable Social Media Content</li>
  <li>Personalized Email Newsletters</li>
  <li>In-depth Whitepapers and E-books</li>
  <li>Compelling Product Descriptions</li>
</ul>

<h4>Our Content Crafting Process:</h4>
<ol>
  <li>Deep-dive research</li>
  <li>Strategic planning</li>
  <li>Expert writing</li>
  <li>Meticulous editing</li>
  <li>SEO optimization</li>
  <li>Ready-to-publish delivery</li>
</ol>

<h4>The Power of Great Content:</h4>
<ul>
  <li>✓ Builds trust and credibility</li>
  <li>✓ Boosts search engine rankings</li>
  <li>✓ Increases website traffic</li>
  <li>✓ Supports marketing and sales</li>
  <li>✓ Establishes thought leadership</li>
</ul>

<p>Let us weave words that work wonders for your brand!</p>
      `,
      color: '#fb5607'
    },
    {
      id: 5,
      title: 'Video Production',
      image: '/assets/images/video-production.jpg',
      description: 'High-impact video content that tells your story and showcases your products to captivate your audience.',
      icon: '🎬',
      details: `
<h3>Lights, Camera, Conversion!</h3>

<p>From concept to final cut, our video production service creates high-impact visual content that tells your story, showcases your products, and captivates your audience.</p>

<h4>Video Styles We Master:</h4>
<ul>
  <li>Corporate Brand Stories</li>
  <li>Engaging Product Demos</li>
  <li>Animated Explainer Videos</li>
  <li>Authentic Customer Testimonials</li>
  <li>Dynamic Event Coverage</li>
  <li>Viral-worthy Social Media Clips</li>
</ul>

<h4>Our Production Journey:</h4>
<ol>
  <li>Concept Development: Crafting your video vision</li>
  <li>Scriptwriting: Perfecting your message</li>
  <li>Storyboarding: Visualizing each frame</li>
  <li>Production: Lights, camera, action!</li>
  <li>Post-Production: Where the magic happens</li>
  <li>Final Revisions: Polishing to perfection</li>
</ol>

<h4>Why Our Videos Shine:</h4>
<ul>
  <li>✓ State-of-the-art equipment</li>
  <li>✓ Experienced production crew</li>
  <li>✓ Creative storytelling expertise</li>
  <li>✓ Tailored to your budget and timeline</li>
  <li>✓ Seamless project management</li>
</ul>

<p>Ready to bring your story to life on screen?</p>
      `,
      color: '#ffbe0b'
    },
    {
      id: 6,
      title: 'Facebook Marketing',
      image: '/assets/images/facebook-marketing.png',
      description: 'Targeted Facebook campaigns that reach your ideal customers and maximize your ROI.',
      icon: '👥',
      details: `
<h3>Unlock the Power of Facebook</h3>

<p>Harness the world's largest social network to skyrocket your business growth. Our Facebook marketing experts create targeted campaigns, develop engaging content, and leverage data-driven insights to maximize your ROI.</p>

<h4>Our Facebook Domination Strategy:</h4>
<ol>
  <li>Laser-focused audience targeting</li>
  <li>Custom-tailored marketing blueprints</li>
  <li>Scroll-stopping content creation</li>
  <li>Strategic ad campaign management</li>
  <li>Active community engagement</li>
  <li>In-depth performance analysis</li>
</ol>

<h4>Key Services:</h4>
<ul>
  <li>Facebook Page Optimization</li>
  <li>Targeted Ad Campaigns</li>
  <li>Strategic Content Calendars</li>
  <li>Facebook Pixel Implementation</li>
  <li>A/B Testing for Peak Performance</li>
  <li>Competitive Analysis and Benchmarking</li>
</ul>

<h4>Why Choose Our Facebook Marketing:</h4>
<ul>
  <li>✓ Amplify brand awareness</li>
  <li>✓ Boost audience engagement</li>
  <li>✓ Drive quality website traffic</li>
  <li>✓ Generate valuable leads</li>
  <li>✓ Maximize marketing ROI</li>
  <li>✓ Gain customer behavior insights</li>
</ul>

<p>Ready to make Facebook your most powerful marketing ally?</p>
      `,
      color: '#3a86ff'
    }
  ];

  // Add smooth scroll-to-top effect on mount
  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    };

    // Scroll to top when component mounts
    scrollToTop();

    // Optional: Also handle page refresh
    window.onbeforeunload = () => {
      window.scrollTo(0, 0);
    };

    return () => {
      // Cleanup the event listener
      window.onbeforeunload = null;
    };
  }, []); // Empty dependency array means this runs once on mount

  useEffect(() => {
    if (selectedService) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [selectedService]);

  // Ensure video plays on iOS devices
  useEffect(() => {
    if (videoRef.current) {
      const video = videoRef.current;
      
      // Set required attributes for iOS
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.muted = true;
      video.autoplay = true;
      
      // Force play on iOS
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('Services video autoplay started');
          })
          .catch((error) => {
            console.log('Services video autoplay prevented:', error);
          });
      }
    }
  }, []);


  const handleCardClick = (service) => setSelectedService(service);
  const handleClose = () => setSelectedService(null);

  const Card = ({ service, onClick, isSelected }) => {
    const springConfig = { stiffness: 1000, damping: 25, mass: 0.1 };
    const x = useSpring(0, springConfig);
    const y = useSpring(0, springConfig);

    const rotateX = useTransform(y, [-50, 50], [15, -15]);
    const rotateY = useTransform(x, [-50, 50], [-15, 15]);

    function handleMouse(event) {
      const rect = event.currentTarget.getBoundingClientRect();
      const mouseX = (event.clientX - rect.left - rect.width / 2) * 2;
      const mouseY = (event.clientY - rect.top - rect.height / 2) * 2;
      x.set(mouseX);
      y.set(mouseY);
    }

    function handleMouseLeave() {
      x.set(0, true);
      y.set(0, true);
    }

    return (
      <motion.div
        className={`hs-service-card ${isSelected ? 'hidden' : ''}`}
        onClick={() => onClick(service)}
        onMouseMove={handleMouse}
        onMouseLeave={handleMouseLeave}
        style={{
          x,
          y,
          rotateX,
          rotateY,
          z: 100,
          background: `linear-gradient(135deg, ${service.color}22, ${service.color}44)`,
          zIndex: isSelected ? 1000 : 1,
          opacity: isSelected ? 0 : 1,
          pointerEvents: isSelected ? 'none' : 'auto',
          border: `1px solid ${service.color}33`,
          boxShadow: `0 10px 20px rgba(0, 0, 0, 0.2), 0 5px 15px ${service.color}33`
        }}
        whileHover={{
          scale: 1.05,
          boxShadow: `0 15px 30px rgba(0, 0, 0, 0.3), 0 10px 25px ${service.color}55`,
          transition: { duration: 0.2 }
        }}
        whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
        layoutId={`card-${service.id}`}
      >
        <motion.div className="hs-card-content">
          <motion.div className="hs-image-container">
            <motion.div className="hs-service-icon" style={{ backgroundColor: `${service.color}99` }}>
              <span>{service.icon}</span>
            </motion.div>
            <motion.img
              src={service.image}
              alt={service.title}
              layoutId={`image-${service.id}`}
              whileHover={{ scale: 1.1, transition: { duration: 0.2 } }}
            />
            <motion.div className="hs-image-overlay" style={{ background: `linear-gradient(to bottom, rgba(0,0,0,0) 0%, ${service.color}99 100%)` }}></motion.div>
          </motion.div>
          <motion.h2
            layoutId={`title-${service.id}`}
            style={{ color: service.color }}
          >
            {service.title}
          </motion.h2>
          <motion.div className="hs-card-divider" style={{ backgroundColor: service.color }}></motion.div>
          <motion.p className="hs-card-brief-description">
            {service.description}
          </motion.p>
          <motion.div className="hs-card-footer">
            <motion.span className="hs-learn-more" style={{ color: service.color }}>
              Learn More <span className="hs-arrow">→</span>
            </motion.span>
          </motion.div>
        </motion.div>
      </motion.div>
    );
  };

  const ExpandedCard = ({ service, onClose }) => (
    <motion.div
      className="hs-expanded-card-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5, ease: "easeInOut" }}
      onClick={onClose}
    >
      <motion.div
        className="hs-expanded-card"
        layoutId={`card-${service.id}`}
        onClick={(e) => e.stopPropagation()}
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{
          type: "spring",
          stiffness: 100,
          damping: 20,
          duration: 0.5,
          ease: "easeInOut",
        }}
        style={{
          background: `linear-gradient(135deg, ${service.color}22, ${service.color}44)`,
          border: `1px solid ${service.color}33`,
          boxShadow: `0 15px 40px rgba(0, 0, 0, 0.5), 0 0 20px ${service.color}33`
        }}
      >
        <motion.div className="hs-expanded-content">
          <motion.div className="hs-image-container">
            <motion.div className="hs-service-icon-expanded" style={{ backgroundColor: `${service.color}99` }}>
              <span>{service.icon}</span>
            </motion.div>
            <motion.img
              src={service.image}
              alt={service.title}
              layoutId={`image-${service.id}`}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            />
            <motion.div
              className="hs-image-overlay"
              style={{ background: `linear-gradient(to bottom, rgba(0,0,0,0) 0%, ${service.color}99 100%)` }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.7 }}
              transition={{ duration: 0.6 }}
            ></motion.div>
          </motion.div>

          <motion.h2
            className="hs-expanded-title"
            layoutId={`title-${service.id}`}
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            style={{ color: service.color }}
          >
            {service.title}
          </motion.h2>

          <motion.div
            className="hs-card-divider-expanded"
            style={{ backgroundColor: service.color }}
            initial={{ width: 0 }}
            animate={{ width: '80px' }}
            transition={{ delay: 0.3, duration: 0.6 }}
          ></motion.div>

          <motion.div
            className="hs-expanded-scrollable-text"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6, ease: "easeOut" }}
            dangerouslySetInnerHTML={{ __html: service.details }}
          />

          <motion.div className="hs-expanded-footer">
            <Link to="/contact">
              <motion.button
                className="hs-3d-button hs-expanded-button"
                style={{
                  background: `linear-gradient(to right, ${service.color}DD, ${service.color})`,
                  color: '#FFFFFF',
                  boxShadow: `0px 4px 12px rgba(0, 0, 0, 0.3), 0px 2px 6px ${service.color}55`
                }}
                whileHover={{
                  scale: 1.03,
                  boxShadow: `0px 6px 15px rgba(0, 0, 0, 0.35), 0px 4px 10px ${service.color}77`,
                }}
                whileTap={{
                  scale: 0.98,
                  boxShadow: `inset 0px 3px 6px rgba(0, 0, 0, 0.25)`,
                }}
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.4, ease: "easeOut" }}
              >
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ marginRight: '8px', fontSize: '1.1rem' }}>📞</span> Contact for Details
                </span>
              </motion.button>
            </Link>
          </motion.div>
        </motion.div>

        <motion.button
          className="hs-close-button"
          onClick={onClose}
          whileHover={{ scale: 1.2, rotate: 90 }}
          whileTap={{ scale: 0.9 }}
          initial={{ opacity: 0, rotate: -90 }}
          animate={{ opacity: 1, rotate: 0 }}
          exit={{ opacity: 0, rotate: 90 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          &times;
        </motion.button>
      </motion.div>
    </motion.div>
  );

  return (
    <>
      <video 
        ref={videoRef}
        autoPlay 
        muted 
        loop 
        playsInline
        webkit-playsinline="true"
        preload="auto"
        className="hs-background-video"
        style={{ pointerEvents: 'none' }}
      >
        <source src="/assets/video/full-backgroundvid.mp4" type="video/mp4" />
      </video>

      <ScrollProgressIndicator />

      <motion.div
        ref={ref}
        className="hs-services-page"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="hs-services-header">
          <motion.h1
            className='hs-Service-heading'
            initial={{ y: -50, opacity: 0 }}
            animate={inView ? { y: 0, opacity: 1 } : {}}
            transition={{ type: "spring", damping: 10, stiffness: 100 }}
          >
            Our Services
          </motion.h1>
          <motion.p
            initial={{ y: 50, opacity: 0 }}
            animate={inView ? { y: 0, opacity: 1 } : {}}
            transition={{ delay: 0.2, type: "spring", damping: 10, stiffness: 100 }}
          >
            Discover our comprehensive suite of professional services tailored to transform your digital presence.
            From stunning web design to strategic marketing solutions, we deliver exceptional results that help your business thrive.
          </motion.p>
        </div>
        <motion.div
          className="hs-container"
          layout
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ staggerChildren: 0.1 }}
        >
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.1 }}
            >
              <Card
                service={service}
                onClick={handleCardClick}
                isSelected={selectedService === service}
              />
            </motion.div>
          ))}
        </motion.div>
        <AnimatePresence>
          {selectedService && <ExpandedCard service={selectedService} onClose={handleClose} />}
        </AnimatePresence>

        <BookingSection />
        <OurPartnersSection />
      </motion.div>
    </>
  );
};

export default ServicesPage;